// Makkerspil i browseren: kobler RoomClient til Game, kortet og skærmene. Rummet er sandheden;
// denne skærm sender intents og afspiller de ops, rummet sender tilbage (DESIGN.md: Online).

import { advise, adviceIntent, opsTip } from '../ai/coach';
import type { Sfx } from '../audio/sfx';
import type { Game } from '../game';
import type { P } from '../render/geo';
import type { Scene } from '../render/scene';
import { hash, legalActions } from '../sim';
import type { ActionKind, ApplyError, Intent, State } from '../sim';
import { announce } from '../ui/a11y';
import { CLAIM_MS, CoopLayer, pingLabel } from '../ui/coop';
import type { GhostShape } from '../ui/coop';
import { BAD_LINK, LobbyScreen, ROOM_ERROR, roomEntryScreen } from '../ui/screens/room';
import type { EntryOpts, RoomError } from '../ui/screens/room';
import type { Screens } from '../ui/screens/screens';
import type { Toast } from '../ui/toast';
import { CURSOR_MS, isTickOp, opIntents } from './protocol';
import type { DifficultyId } from '../sim';
import type { ClientMsg, Op, PingAt, PingKind, RoomView, Seat, WorldP } from './protocol';
import { RoomClient } from './room-client';
import type { NetStatus } from './room-client';

export interface OnlineHost {
  game: Game;
  scene: Scene;
  stage: HTMLElement;
  root: HTMLElement;
  screens: Screens;
  toast: Toast;
  sfx: Sfx;
  /** Et lille ryk i telefonen (følger indstillingen Vibration og lyd fra). */
  vibrate?(pattern: number | number[]): void;
  /** En makkerbane begynder på denne skærm (start eller genindlæsning midt i): læg kortet ud og spil. */
  enter(): void;
  /** Ny state midt i banen (hash-forskel eller genforbindelse). */
  resynced(): void;
  /** Banen er slut i rummet, og I er tilbage i lobbyen. */
  leftGame(): void;
  /** Rummet afviste en handling. */
  fail(kind: ActionKind | null, error: ApplyError | 'paused'): void;
  /** Rummet er forladt: tilbage til titlen. */
  title(): void;
}

/** Afvisninger, spilleren ikke skal se (gentagelser og handlinger, der kom for sent). */
const QUIET = new Set(['duplicate', 'wrong_phase']);
/** Står mere end så mange ticks i kø (fx efter baggrund), henter vi et frisk snapshot i stedet. */
const MAX_BEHIND = 60;

const isSeat = (s: unknown): s is Seat => s === 'p1' || s === 'p2';

export class Online {
  client: RoomClient | null = null;
  room: RoomView | null = null;
  you: Seat | null = null;
  /** En makkerbane kører på denne skærm. */
  inGame = false;
  /** Rummets run, da denne skærm sidst gik ind i en bane (RoomView.run). */
  private runIn = -1;
  readonly coop: CoopLayer;
  private lobby: LobbyScreen | null = null;
  private lastSeq = 0;
  private sent = new Map<number, ActionKind>();
  /** Op-nummer (efter afspilning) til egne clientSeq i det op: så forsvinder spøgelset. */
  private mine = new Map<number, number[]>();
  /** Egne træk, rummet har godkendt (til e2e). */
  private built = 0;
  /** Det, makkeren har sagt "Jeg tager den" til (node-, ledning- eller trussel-id), og hvornår det slipper. */
  private claims = new Map<string, number>();
  private cursorRaw: WorldP | null = null;
  private cursorSeen = 0;
  private cursorSent = 0;
  private cursorQueued: P | null | undefined;
  private stageRect: DOMRect | null = null;
  private busy = false;
  lastSnap: { n: number; hash: string } | null = null;

  constructor(private host: OnlineHost) {
    this.coop = new CoopLayer(host.stage, {
      locate: (at) => this.locate(at),
      anchor: (p) => this.anchor(p),
      ping: (kind, at, to, act) => this.sendPing(kind, at, to, act),
      live: () => this.live,
      onNode: (p) => this.host.scene.hitNode(this.toStage(p)) !== null,
      vibrate: (ms) => this.host.vibrate?.(ms),
    });
    this.coop.onPointer = (p) => this.queueCursor(p);
    host.root.append(this.coop.el, this.coop.wheelEl);
    window.addEventListener('resize', () => {
      this.stageRect = null;
    });
  }

  get active(): boolean {
    return this.client !== null;
  }

  /** Må man pinge og se makkeren lige nu: en makkerbane uden skærm foran. */
  get live(): boolean {
    return this.inGame && this.host.screens.current === null && !this.host.game.over;
  }

  // -------------------------------------------------------------------------
  // Indgang og rum

  /** Fra forsiden (uden kode) eller et link (med kode). */
  open(code?: string | null): void {
    if (code) this.join(code);
    else this.showEntry();
  }

  /** Et makkerlink med en kode, der ikke kan være rigtig: sig det i stedet for bare at vise forsiden. */
  openBadLink(): void {
    try {
      history.replaceState(null, '', '/');
    } catch {
      // Ligegyldigt.
    }
    this.showEntry(null, '', { message: BAD_LINK });
  }

  private showEntry(error: RoomError | null = null, code = '', extra: Pick<EntryOpts, 'message' | 'fix'> = {}): void {
    this.host.screens.show(
      'room',
      roomEntryScreen(
        {
          create: () => void this.create(),
          join: (c) => {
            this.host.sfx.play('tap');
            this.join(c);
          },
          back: () => {
            this.host.sfx.play('tap');
            this.leave();
            this.host.title();
          },
        },
        { busy: this.busy, error, code, ...extra },
      ),
    );
  }

  async create(): Promise<void> {
    if (this.busy) return;
    this.host.sfx.play('tap');
    this.busy = true;
    this.showEntry();
    let err: RoomError | null = null;
    let code: string | null = null;
    try {
      const r = await fetch('/api/room', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
      const j = (await r.json().catch(() => null)) as { ok?: boolean; code?: string; error?: string } | null;
      if (r.ok && j?.ok && typeof j.code === 'string') code = j.code;
      else err = r.status === 429 || j?.error === 'rate' ? 'rate' : 'bad';
    } catch {
      err = 'net';
    }
    this.busy = false;
    if (code) this.join(code);
    else this.showEntry(err);
  }

  join(code: string): void {
    this.dispose();
    try {
      history.replaceState(null, '', `/r/${code}`);
    } catch {
      // Ligegyldigt: linket i adresselinjen er kun en bekvemmelighed.
    }
    this.lobby = this.makeLobby(code, false);
    this.host.screens.show('room', this.lobby.el);
    this.client = new RoomClient(code, {
      status: (s) => this.onStatus(s),
      welcome: (you, room, lastSeq) => this.onWelcome(you, room, lastSeq),
      room: (room) => this.onRoom(room),
      snap: (state, n, paused, speed, ready) => {
        this.onSnap(state, n, paused !== null, speed);
        this.host.game.yearWait = paused === 'year' ? { ready: (ready ?? []).filter(isSeat), goAt: null } : null;
      },
      ops: (ops) => this.onOps(ops),
      ack: (seq, ok, reason) => this.onAck(seq, ok, reason),
      ping: (m) => this.onPing(m.id, m.from, m.kind, m.at, m.to, m.act),
      cursor: (from, p) => {
        if (from === this.you) return;
        this.cursorRaw = p;
        this.cursorSeen = performance.now();
      },
      ctl: (paused, speed, by, ready, go) => {
        if (paused === 'year') return this.onYear(speed, by, (ready ?? []).filter(isSeat), go);
        this.host.game.yearWait = null;
        this.onCtl(paused !== null, paused === 'absent', speed, by);
      },
      refused: (code) => this.host.toast.say(ROOM_ERROR[code], 'info', 2200),
      fatal: (code) => this.onFatal(code),
    });
  }

  private makeLobby(code: string, inGame: boolean): LobbyScreen {
    return new LobbyScreen(
      code,
      {
        setup: (level?: string, difficulty?: DifficultyId) => {
          this.host.sfx.play('tap');
          const m: ClientMsg = { t: 'setup' };
          if (level) m.level = level;
          if (difficulty) m.difficulty = difficulty;
          this.client?.send(m);
        },
        start: () => {
          this.host.sfx.play('tap');
          this.client?.send({ t: 'start' });
        },
        swap: () => {
          this.host.sfx.play('tap');
          this.client?.send({ t: 'swap' });
        },
        leave: () => {
          this.host.sfx.play('tap');
          this.leave();
          this.host.title();
        },
        resume: inGame ? () => this.resume() : undefined,
      },
      inGame,
    );
  }

  /** Spillets menu-knap: rummet med kode og makker, mens banen kører videre bagved. */
  menu(): void {
    if (!this.client) return;
    this.lobby = this.makeLobby(this.client.code, true);
    this.host.screens.show('room', this.lobby.el);
    this.refreshLobby();
  }

  private resume(): void {
    this.host.sfx.play('tap');
    this.host.screens.hide();
    this.host.stage.focus({ preventScroll: true });
  }

  /** Esc: tilbage til banen fra menuen, eller fra indgangen til titlen. */
  escape(): boolean {
    if (this.host.screens.current !== 'room') return false;
    if (this.inGame) this.resume();
    else if (!this.client) {
      this.host.title();
    }
    return true;
  }

  /** Forlad rummet for godt. */
  leave(): void {
    this.client?.leave();
    this.client = null;
    this.reset();
    try {
      if (location.pathname !== '/') history.replaceState(null, '', '/');
    } catch {
      // Ligegyldigt.
    }
  }

  /** Luk forbindelsen uden at opgive sædet. */
  private dispose(): void {
    this.client?.dispose();
    this.client = null;
    this.reset();
  }

  private reset(): void {
    const g = this.host.game;
    g.remote = null;
    g.player = 'p1';
    g.rolesLive = false;
    g.dropOps();
    this.inGame = false;
    this.room = null;
    this.you = null;
    this.lobby = null;
    this.sent.clear();
    this.mine.clear();
    this.claims.clear();
    this.cursorRaw = null;
    this.coop.show(false);
  }

  // -------------------------------------------------------------------------
  // Fra rummet

  private onStatus(s: NetStatus): void {
    this.refreshLobby();
    if (s === 'reconnecting' && this.inGame) announce('Forbindelsen hakker. Vi prøver igen.');
  }

  private onWelcome(you: Seat, room: RoomView, lastSeq: number): void {
    this.you = you;
    this.lastSeq = lastSeq;
    this.host.game.seqAtLeast(lastSeq);
    this.coop.setSeat(you);
    this.onRoom(room);
  }

  private onRoom(room: RoomView): void {
    const before = this.room;
    this.room = room;
    // Samme regel som rummet: rollerne gælder, mens begge er forbundet.
    this.host.game.rolesLive = !!room.seats.p1?.on && !!room.seats.p2?.on;
    const you = this.you;
    if (!you) return;
    const other: Seat = you === 'p1' ? 'p2' : 'p1';
    const was = before?.seats[other]?.on ?? false;
    const now = room.seats[other]?.on ?? false;
    if (before && was !== now) {
      if (now) {
        this.host.sfx.play('ding');
        // Rummet fortsætter aldrig af sig selv (IGN runde 2): sig, hvem der skal trykke.
        const wait = this.inGame && this.host.game.paused;
        this.host.toast.say(before.seats[other] ? (wait ? 'Makker er tilbage! Tryk afspil, når I er klar.' : 'Makker er tilbage!') : 'Makker er her!', 'good', wait ? 3200 : 2000);
        announce('Din makker er med.');
      } else {
        // IGN-løkke 2, runde 3 (co-op): værten trykkede Forsiden på resultatet, og gæsten stod tilbage
        // uden at vide det. Nu står det på resultatkortet, hvad der skete, og hvad knapperne gør.
        if (!room.seats[other] && this.inGame && this.host.game.over) this.noteOnResult('Makker gik til forsiden. Prøv igen eller Næste bane venter på en ny makker i rummet.');
        const free = this.inGame && this.host.game.state.roles ? ' Du må alt imens.' : '';
        this.host.toast.say(`${room.seats[other] ? 'Makker mistede forbindelsen. Pladsen venter.' : 'Makker forlod rummet.'}${free}`, 'info', free ? 3400 : 2600);
        announce('Din makker er væk.');
      }
    }
    if (room.phase === 'lobby' && this.inGame) {
      // Banen er slut, og en af jer trykkede videre: alle tilbage i rummet.
      this.inGame = false;
      this.host.game.remote = null;
      this.host.game.rolesLive = false;
      this.host.game.dropOps();
      this.coop.show(false);
      this.host.leftGame();
      this.lobby = null;
    }
    if (room.phase === 'lobby' && (!this.lobby || this.host.screens.current !== 'room')) {
      this.lobby = this.makeLobby(room.code, false);
      this.host.screens.show('room', this.lobby.el);
    }
    this.refreshLobby();
  }

  /** En kort besked på resultatkortet lige over knapperne (toasts står ikke over skærmene). */
  private noteOnResult(text: string): void {
    const card = this.host.screens.el.querySelector<HTMLElement>('.card.result');
    if (!card) return;
    let note = card.querySelector<HTMLElement>('.result-partner');
    if (!note) {
      note = document.createElement('p');
      note.className = 'result-partner';
      note.setAttribute('role', 'status');
      const actions = card.querySelector('.actions');
      if (actions) card.insertBefore(note, actions);
      else card.append(note);
    }
    note.textContent = text;
  }

  private refreshLobby(): void {
    const net = this.client?.status ?? 'closed';
    if (this.lobby && this.room && this.you) this.lobby.update(this.room, this.you, net);
    if (this.room && this.you) {
      const other: Seat = this.you === 'p1' ? 'p2' : 'p1';
      this.coop.setStatus(this.room.seats[other]?.on ?? false, net);
    }
  }

  private remote = {
    send: (i: Intent) => this.sendIntent(i),
    applied: (n: number, state: State) => {
      this.client?.applied(n, state);
      const seqs = this.mine.get(n);
      if (seqs) {
        this.mine.delete(n);
        for (const s of seqs) this.coop.removeGhost(s);
      }
    },
  };

  private onSnap(state: State, n: number, paused: boolean, speed: 1 | 2): void {
    const g = this.host.game;
    const you = this.you ?? 'p1';
    g.player = you;
    g.remote = this.remote;
    g.loadRemote(state, n, this.lastSeq);
    g.paused = paused;
    g.speed = speed;
    this.mine.clear();
    this.coop.clear();
    this.lastSnap = { n, hash: hash(state) };
    // IGN-løkke 2, runde 3 (co-op blocker): "Prøv igen" og "Næste bane" starter en ny bane i rummet
    // uden lobby imellem. Er det en ny bane (run er talt op), går begge skærme samme vej ind som fra
    // lobbyen: resultatkortet lukkes, og holdets logbog starter forfra.
    const run = this.room?.run ?? 0;
    const fresh = this.inGame && run !== this.runIn;
    this.runIn = run;
    if (fresh) {
      this.coop.show(true);
      this.host.enter();
    } else if (!this.inGame) {
      this.inGame = true;
      this.lobby = null;
      this.coop.show(true);
      this.refreshLobby();
      this.host.enter();
    } else {
      this.host.resynced();
    }
  }

  private onOps(ops: Op[]): void {
    const client = this.client;
    if (!client || !this.inGame) return;
    const g = this.host.game;
    const base = client.n - ops.length;
    const you = this.you;
    g.enqueue(
      ops.map((op, i) => {
        const intents = opIntents(op);
        const seqs = intents.filter((x) => x.player === you).map((x) => x.clientSeq);
        if (seqs.length > 0) this.mine.set(base + i + 1, seqs);
        return { tick: isTickOp(op), intents };
      }),
    );
  }

  private onAck(seq: number, ok: boolean, reason?: string): void {
    const kind = this.sent.get(seq) ?? null;
    this.sent.delete(seq);
    if (ok) {
      this.built++;
      return;
    }
    this.coop.removeGhost(seq);
    if (!reason || QUIET.has(reason)) return;
    this.host.fail(kind, reason as ApplyError | 'paused');
  }

  private onPing(id: number, from: Seat, kind: PingKind, at: PingAt, to?: number, act?: ActionKind): void {
    if (!this.inGame) return;
    const mineP = from === this.you;
    const now = performance.now();
    this.coop.showPing(id, from, kind, at, now, true, to, act);
    // IGN-løkke runde 1: "Jeg tager den" holder, til makkeren har haft tid til det, og hintet lader
    // målet være (se claimed). Ens eget pings mål er stadig ens eget.
    if (!mineP && kind === 'mine' && typeof at === 'string') this.claims.set(at, now + CLAIM_MS);
    if (!mineP) {
      this.host.sfx.play('pop');
      announce(`Makker: ${pingLabel(kind, act)}`);
    }
  }

  private onCtl(paused: boolean, absent: boolean, speed: 1 | 2, by?: Seat): void {
    const g = this.host.game;
    const was = g.paused;
    const wasSpeed = g.speed;
    g.paused = paused;
    g.speed = speed;
    if (!this.inGame) return;
    if (by && by !== this.you) {
      if (was !== paused) this.host.toast.say(paused ? 'Makker satte på pause.' : 'Makker trykkede afspil.', 'info', 1800);
      else if (wasSpeed !== speed) this.host.toast.say(speed === 2 ? 'Makker skruede op for farten.' : 'Makker satte farten ned.', 'info', 1800);
    } else if (!by && paused && absent) {
      const other: Seat = this.you === 'p1' ? 'p2' : 'p1';
      const gone = this.room ? this.room.seats[other] && !this.room.seats[other]?.on : false;
      this.host.toast.say(gone ? 'Makker røg af, så spillet venter. Tryk afspil for at spille videre.' : 'I var begge væk, så spillet venter.', 'info', gone ? 4000 : 2400);
    } else if (!by && !paused && was) {
      this.host.toast.say('Spillet kører igen.', 'good', 1600);
    }
    announce(paused ? 'Pause.' : 'Spillet kører.');
  }

  /**
   * Spor: tillid (co-op runde 4: "under et årsskifte med en ny by gik to hjerter, mens begge kiggede på
   * noget andet"). Det nye år venter, til I begge har trykket afspil; har den ene trykket, starter det
   * af sig selv efter YEAR_READY_MS. Hintet siger, hvem der venter (main.ts).
   */
  private onYear(speed: 1 | 2, by: Seat | undefined, ready: Seat[], go: number | undefined): void {
    const g = this.host.game;
    const fresh = !g.yearWait;
    g.paused = true;
    g.speed = speed;
    g.yearWait = { ready, goAt: go === undefined ? null : performance.now() + go };
    if (!this.inGame) return;
    if (fresh) {
      this.host.sfx.play('ding');
      announce('Nyt år. Uret står, til I begge trykker afspil.');
    } else if (by && by !== this.you && ready.includes(by) && !ready.includes(this.you ?? 'p1')) {
      this.host.toast.say('Makker er klar. Tryk afspil, når du er.', 'info', 2400);
      announce('Makker er klar til det nye år.');
    }
  }

  private onFatal(code: RoomError): void {
    const room = this.client?.code ?? null;
    this.client = null;
    const wasIn = this.inGame;
    this.reset();
    if (wasIn) this.host.leftGame();
    // En anden fane tog over: rummet lever stadig, så linket bliver stående, og "Spil her" henter det tilbage.
    const keep = code === 'replaced' && room !== null;
    try {
      if (!keep) history.replaceState(null, '', '/');
    } catch {
      // Ligegyldigt.
    }
    let fix: EntryOpts['fix'];
    if (keep) {
      fix = {
        label: 'Spil her',
        run: () => {
          this.host.sfx.play('tap');
          this.join(room);
        },
      };
    } else if (code === 'version') fix = { label: 'Genindlæs', run: () => location.reload() };
    this.showEntry(code, keep ? room : '', fix ? { fix } : {});
    announce(ROOM_ERROR[code], true);
  }

  // -------------------------------------------------------------------------
  // Til rummet

  private sendIntent(i: Intent): void {
    const client = this.client;
    if (!client || !client.intent(i)) {
      this.host.toast.say('Ingen forbindelse lige nu. Prøv igen om lidt.', 'warn', 2200);
      return;
    }
    this.sent.set(i.clientSeq, i.kind);
    const shape = ghostShape(i);
    if (shape) this.coop.addGhost(i.clientSeq, shape, performance.now());
  }

  /** Ping et mål (node, ledning eller fare) fra hintet: "det er dit job, her". */
  pingAt(kind: PingKind, id: string): void {
    if (!this.inGame || this.host.game.over) return;
    this.sendPing(kind, id);
  }

  private sendPing(kind: PingKind, at: PingAt, to?: number, act?: ActionKind): void {
    this.host.sfx.play('tap');
    this.client?.ping(kind, at, undefined, to, act);
  }

  /**
   * Spor: coop: bed makkeren om noget, kun den anden rolle må ("Byg her?"). Et ærligt første tryk:
   * Driftsvagten får aldrig en byggeflade, der afvises bagefter, men en knap, der spørger.
   */
  ask(at: string, act: ActionKind): boolean {
    if (!this.live || this.host.game.over) return false;
    this.coop.ask(at, act);
    return true;
  }

  togglePause(): void {
    if (!this.inGame || this.host.game.over) return;
    this.client?.send({ t: 'ctl', pause: !this.host.game.paused });
    this.host.sfx.play('tap');
  }

  toggleSpeed(): void {
    if (!this.inGame) return;
    this.client?.send({ t: 'ctl', speed: this.host.game.speed === 1 ? 2 : 1 });
  }

  /**
   * Efter resultatet: tilbage i rummet, evt. med næste bane valgt (begge makkere må). `now`: start
   * med det samme (co-op-anmelderen, IGN-løkke runde 3: "Prøv igen" sendte jer til lobbyen).
   */
  again(nextLevel?: string, now = false): void {
    const c = this.client;
    if (!c) return;
    const m: ClientMsg = { t: 'again' };
    if (nextLevel) m.level = nextLevel;
    if (now) m.go = true;
    c.send(m);
  }

  private queueCursor(p: P | null): void {
    if (!this.live || !this.client) return;
    this.cursorQueued = p;
  }

  // -------------------------------------------------------------------------
  // Kort og skærm: makkerens punkter går via nærmeste node, fordi kortet lægges lidt forskelligt
  // ud på hver skærmstørrelse.

  private rect(): DOMRect {
    this.stageRect ??= this.host.stage.getBoundingClientRect();
    return this.stageRect;
  }

  /** Skærmpunkt i kortets lokale koordinater (fra vinduet). */
  private toStage(p: P): P {
    const r = this.rect();
    return { x: p.x - r.left, y: p.y - r.top };
  }

  private fromStage(p: P | null): P | null {
    if (!p) return null;
    const r = this.rect();
    return { x: p.x + r.left, y: p.y + r.top };
  }

  /** Nodens visningsposition i km (kortet skubber noder fra hinanden for luftens skyld). */
  private disp(id: string): P | null {
    const sc = this.host.scene;
    const s = sc.nodeScreen(id);
    return s ? sc.cam.toWorld({ x: s.x, y: s.y + 14 * sc.k }) : null;
  }

  private toRaw(win: P): WorldP | null {
    const sc = this.host.scene;
    const w = sc.cam.toWorld(this.toStage(win));
    let best: { x: number; y: number; d: P } | null = null;
    let bd = Infinity;
    for (const n of this.host.game.state.nodes) {
      if (!n.active) continue;
      const d = this.disp(n.id);
      if (!d) continue;
      const dist = Math.hypot(d.x - w.x, d.y - w.y);
      if (dist < bd) {
        bd = dist;
        best = { x: n.x, y: n.y, d };
      }
    }
    if (!best) return null;
    const r = (v: number): number => Math.round(v * 100) / 100;
    return [r(best.x + w.x - best.d.x), r(best.y + w.y - best.d.y)];
  }

  private fromRaw(raw: WorldP): P | null {
    let best: { id: string; x: number; y: number } | null = null;
    let bd = Infinity;
    for (const n of this.host.game.state.nodes) {
      if (!n.active) continue;
      const dist = Math.hypot(n.x - raw[0], n.y - raw[1]);
      if (dist < bd) {
        bd = dist;
        best = n;
      }
    }
    if (!best) return null;
    const d = this.disp(best.id);
    if (!d) return null;
    return this.fromStage(this.host.scene.cam.toScreen({ x: d.x + raw[0] - best.x, y: d.y + raw[1] - best.y }));
  }

  locate(at: PingAt): P | null {
    if (typeof at !== 'string') return this.fromRaw(at);
    const sc = this.host.scene;
    const s = this.host.game.state;
    if (s.nodes.some((n) => n.id === at)) return this.fromStage(sc.nodeScreen(at));
    if (s.lines.some((l) => l.id === at)) return this.fromStage(sc.lineScreen(at));
    return this.fromStage(sc.threatScreen(at));
  }

  private anchor(win: P): PingAt | null {
    const sc = this.host.scene;
    const p = this.toStage(win);
    // En by lidt ved siden af fingeren vinder over ledningen under den: pingen ved Grindsted landede
    // ellers midt på ledningen mod Endrup (IGN-løkke runde 4).
    return sc.hitNode(p) ?? sc.hitThreat(p) ?? sc.hitNode(p, 24) ?? sc.hitLine(p) ?? this.toRaw(win);
  }

  // -------------------------------------------------------------------------

  /** Hvert billede, før spillet afspiller ops: indhent efter baggrund, markør og pings. */
  frame(now: number, dt: number): void {
    if (!this.client) return;
    const g = this.host.game;
    if (this.inGame && g.pendingTicks > MAX_BEHIND) {
      // Langt bagud (fanen sov): spring frem til rummets state i stedet for at spole effekterne igennem.
      g.dropOps();
      this.mine.clear();
      this.coop.clear();
      this.client.sync();
    }
    if (this.cursorQueued !== undefined && now - this.cursorSent >= CURSOR_MS) {
      const p = this.cursorQueued;
      this.cursorQueued = undefined;
      this.cursorSent = now;
      this.client.cursor(p ? this.toRaw(p) : null);
    }
    if (!this.inGame) return;
    this.coop.cursorTarget(this.cursorRaw && this.live ? this.fromRaw(this.cursorRaw) : null, this.cursorSeen);
    this.coop.frame(now, dt);
  }

  /** Til e2e og fejlfinding (window.__net). Ingen hemmeligheder: token bliver i room-client. */
  debug(): Record<string, unknown> {
    const c = this.client;
    const g = this.host.game;
    return {
      code: c?.code ?? null,
      seat: this.you,
      status: c?.status ?? 'closed',
      inGame: this.inGame,
      phase: this.room?.phase ?? null,
      partner: this.room && this.you ? (this.room.seats[this.you === 'p1' ? 'p2' : 'p1']?.on ?? false) : false,
      n: c?.n ?? 0,
      opN: g.opN,
      pending: g.queued,
      desyncs: c?.desyncs ?? 0,
      built: this.built,
      checked: c?.checked ?? 0,
      hashes: c?.hashes ?? 0,
      lastCheck: c?.lastCheck ?? null,
      rtt: c ? [...c.rtt] : [],
      paused: g.paused,
      lastSnap: this.lastSnap,
    };
  }

  /** Har makkeren sagt "Jeg tager den" til et af målene inden for de sidste CLAIM_MS? */
  claimed(ids: readonly string[], now = performance.now()): boolean {
    if (!this.inGame) return false;
    return ids.some((id) => (this.claims.get(id) ?? 0) > now);
  }

  /** Det, træneren ville gøre nu, som intent (til e2e: byg noget gyldigt fra begge skærme). */
  adviceIntent(): { kind: ActionKind; target?: string; opt?: string } | null {
    const g = this.host.game;
    const s = g.state;
    const you = this.you ?? 'p1';
    // Med roller: trænerens råd kan være makkers job; så Driftsvagtens eget tip, ellers et gyldigt træk i egen rolle.
    let i = [advise(s), opsTip(s)]
      .map((a) => (a ? adviceIntent(s, a, you) : null))
      .find((x) => x && !g.theirs(x.kind));
    if (!i) {
      const c = legalActions(s, you).find((x) => !['wait', 'pick_pack', 'demolish', 'power_shed'].includes(x.kind) && !g.theirs(x.kind));
      if (c) i = { player: you, clientSeq: 0, kind: c.kind, source: 'bot', ...(c.target !== undefined ? { target: c.target } : {}), ...(c.opt !== undefined ? { opt: c.opt } : {}) };
    }
    if (!i) return null;
    const out: { kind: ActionKind; target?: string; opt?: string } = { kind: i.kind };
    if (i.target !== undefined) out.target = i.target;
    if (i.opt !== undefined) out.opt = i.opt;
    return out;
  }

  sync(): void {
    this.client?.sync();
  }
}

function ghostShape(i: Intent): GhostShape | null {
  if (!i.target) return null;
  if (i.kind === 'build_line') {
    const [a, b] = i.target.split('|');
    return a && b ? { line: [a, b] } : null;
  }
  return { at: i.target };
}
